import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, Params } from '@angular/router';

import { ShowcaseFixtureData } from '@cds/betting-offer/domain-specific/showcase';
import { MarqueeRequest } from '@cds/query-objects';
import {
    ClientCachingConfig,
    PrettyUrlsConfig,
    Sitecore,
    SportsConfig,
    TeamPagesConfig,
    TimeFilterConfig,
} from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import {
    CountItem,
    ItemCount,
    ItemMeta,
    ItemType,
    VirtualCompetitionPrefix,
    children,
    isAntepost,
    isVirtualCompetitionItem,
} from '@frontend/sports/common/core/data-access/sport-model';
import { ExpiringCache } from '@frontend/sports/common/core/utils/expiring-cache';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { GridSorting } from '@frontend/sports/grid/core/feature/model';
import { GridStoreService } from '@frontend/sports/grid/core/feature/store';
import { DeviceService } from '@frontend/vanilla/core';
import { flatten, isArray, isEmpty, isNumber, keys, lowerCase, now, orderBy, toInteger } from 'lodash-es';
import { Observable, combineLatest, from, iif, map, mergeMap, of, switchMap, tap } from 'rxjs';

import { CalendarCacheService } from '../../../calendar/calendar-cache.service';
import { CalendarService } from '../../../calendar/calendar.service';
import { IntervalFilter, IntervalLink } from '../../../calendar/count-interval.service';
import { MarqueeApi } from '../../../cds/marquee-api.service';
import { ShowcaseApi } from '../../../cds/showcase-api.service';
import { getItemTypeForSport } from '../../../competition-list/competition-list.models';
import { CompetitionNavigationComponent } from '../../../competition-list/competition-navigation.component';
import { CompetitionRouteService } from '../../../competition-list/competition-route.service';
import { CompetitionTreeService } from '../../../competition-list/competition-tree.service';
import { CompetitionListService, CompetitionOverview } from '../../../competition-list/services/competition-list.service';
import { MasterDetailComponent, MasterDetailDependencyService } from '../../../dependency/master-detail-dependency.service';
import { IMarqueeRequestInfo, MarqueeRequestBuilderService } from '../../../highlights-marquee/marquee-request-builder.service';
import { MarqueeResponseMapperService } from '../../../highlights-marquee/marquee-response-mapper.service';
import { CompetitionPageType } from '../../../highlights-marquee/marquee-tile.model';
import { ColumnLayoutService } from '../../../layout/column-layout.service';
import { RedirectHelperService } from '../../../navigation-core/redirect-helper.service';
import { MarketOfferUrlParam } from '../../../navigation-core/url-helper.service';
import { CompetitionRoute } from '../../../navigation/navigation.models';
import { SeoContentService } from '../../../seo/seo-content.service';
import { ShowcaseRequestBuilderService } from '../../../showcase/showcase-request-builder.service';
import { Lobby, type LobbySpecificRequest, type ShowcaseModuleData } from '../../../showcase/showcase.model';
import { ShowcaseService } from '../../../showcase/showcase.service';
import { TabBarItem } from '../../../tab-bar/tab-bar.models';
import { TeamPagesCoreService } from '../../../teampages-core/team-pages-core.service';
import { TeamPagesViewModel } from '../../../teampages-core/team-pages.model';
import { EventListService } from '../services/event-list.service';
import { SortingStateService } from '../services/sorting-state.service';
import { VirtualCompetitionService } from '../virtual-competition.service';
import { CompetitionGridHelper } from './competition-grid.helper';
import { CompetitionIdService } from './competition-id.service';
import { FixtureList, FixtureTab, ReverseFixtureTab } from './competition.models';
import { PruneHelperService } from './prune-helper.service';

type CountValue = ItemCount & ItemMeta;
type CountCallback = (item: CountValue) => number | undefined;

@Injectable({ providedIn: 'root' })
export class CompetitionService extends MasterDetailDependencyService<CompetitionOverview, FixtureList> {
    private cache: ExpiringCache;
    constructor(
        private service: EventListService,
        private competitionListService: CompetitionListService,
        private routerEvents: RouterEventsService,
        private router: CompetitionRouteService,
        private calendar: CalendarService,
        private sitecore: Sitecore,
        private sortingStateService: SortingStateService,
        private urlConfig: PrettyUrlsConfig,
        private sportConfig: SportsConfig,
        private gridStore: GridStoreService,
        private gridHelper: CompetitionGridHelper,
        private pruneHelperService: PruneHelperService,
        private redirectHelper: RedirectHelperService,
        private device: DeviceService,
        layoutService: ColumnLayoutService,
        clientCachingConfig: ClientCachingConfig,
        private showcaseRequestBuilderService: ShowcaseRequestBuilderService,
        private showcaseApiService: ShowcaseApi,
        private showcaseService: ShowcaseService,
        private seoContentService: SeoContentService,
        private marqueeApi: MarqueeApi,
        private marqueeRequestBuilderService: MarqueeRequestBuilderService,
        private marqueeResponseMapperService: MarqueeResponseMapperService,
        private teamPagesConfig: TeamPagesConfig,
        private teamPagesCoreService: TeamPagesCoreService,
        private virtualCompetitionService: VirtualCompetitionService,
        private competitionTreeService: CompetitionTreeService,
        private calendarCacheService: CalendarCacheService,
        private timeFilterConfig: TimeFilterConfig,
        private competitionIdService: CompetitionIdService,
    ) {
        super(layoutService);
        this.cache = new ExpiringCache(clientCachingConfig.regionListTimeout);

        this.routerEvents.currentActivationEnd.pipe(takeUntilDestroyed()).subscribe((route) => {
            if (route && route.snapshot.data.tag !== RouteTag.Competition) {
                this.deregister();
            }
        });
    }

    getRouteParams(): Params {
        return this.router.params();
    }

    getData(route: ActivatedRouteSnapshot, detail?: FixtureList): Observable<CompetitionOverview | undefined> {
        return this.getCompetitionOverview(this.router.params());
    }

    getPreResolvedData(route: ActivatedRouteSnapshot, detail?: FixtureList | undefined): Observable<CompetitionOverview | undefined> {
        return this.getCompetitionOverview(route.params);
    }

    private getCompetitionOverview({ sport, context, conference, league }: CompetitionRoute): Observable<CompetitionOverview | undefined> {
        const interval = this.calendar.getTimeInterval(context) || {};

        if (!sport) {
            throw new Error('Sport should be defined');
        }

        // following fix is needed for golf, that has antepost and so ends up on a redirect loop
        // as the sport is available but expects to redirect to competitions, while it is unable to do so
        // as there are no events
        const source = this.competitionListService
            .getSportTree(
                sport,
                interval.from,
                interval.to,
                this.layoutService.layout.shouldHaveLeftColumn,
                !!conference && !isArray(league) ? league : undefined,
                conference,
            )
            .pipe(
                map((result) => {
                    if (result?.sport && !isAntepost(result.sport)) {
                        return result;
                    }

                    return;
                }),
            );

        if (!interval.from && !interval.to) {
            return source;
        }

        return this.cache.getOrCreate(`${context}-${sport}`, source);
    }

    override shouldUpdateMasterCache(master: CompetitionOverview, route: ActivatedRouteSnapshot): boolean {
        return true;
    }

    override shouldUpdateDetailCache(detail: FixtureList, route: ActivatedRouteSnapshot): boolean {
        return (
            !!detail.sport &&
            !!detail.sport.children &&
            !!detail.sport.children.length &&
            route.params?.region &&
            (detail.sport.children.length !== 1 || detail.sport.children[0].id.toString() !== route.params.region)
        );
    }

    getDetailData(route: ActivatedRouteSnapshot, master?: CompetitionOverview | undefined): Observable<FixtureList> {
        const params = this.mergeTargetToParams(this.router.params(), this.getTarget(master));
        return this.getFixtureList(this.getData(route), route, params);
    }

    getPreResolvedDetailData(route: ActivatedRouteSnapshot): Observable<FixtureList> {
        const params = this.mergeTargetToParams({ ...route.params }, this.getTarget(undefined, route));
        return this.getFixtureList(this.getPreResolvedData(route), route, params, false, false);
    }

    private getFixtureList(
        competitionOverview$: Observable<CompetitionOverview | undefined>,
        route: ActivatedRouteSnapshot,
        params: CompetitionRoute,
        includePostRequest = true,
        shouldRedirect = true,
    ): Observable<FixtureList> {
        const empty = of(undefined);
        const sorting = this.getSorting(params);
        const showcaseRequest = this.getShowcaseRequest(params);

        // we have the cache implemented in the getData method, so we do not care to check if load from cache or reload it
        return competitionOverview$.pipe(
            map((overview) => ({
                sport: overview ? this.pruneHelperService.prune(overview.sport, params) : undefined,
                overview,
                compoundId: this.competitionIdService.getCompoundId(overview?.sport, params?.league),
            })),
            tap(({ sport }) => this.enhance(sport, params, shouldRedirect)),
            switchMap(({ sport, overview, compoundId }) =>
                combineLatest([
                    // When params.conference is 0 it means there is conferenceId in route, but it wasn't parsed successfully
                    // So we skip calling cds for invalid conference
                    // when params.conference is undefined it means  there is no conferenceId in route
                    // and user is not on conference page
                    iif(() => params.conference === 0 || this.isWithoutFixtures(params, sport), empty, this.service.getFixtureList(params, sorting)),
                    iif(
                        () => includePostRequest && !isEmpty(showcaseRequest.queries),
                        this.showcaseApiService.getShowcaseData(showcaseRequest),
                        empty,
                    ),
                    iif(
                        () => includePostRequest,
                        this.getMarqueeRequest(sport, params).pipe(
                            mergeMap(({ request, tiles }) =>
                                iif(
                                    () => isEmpty(request),
                                    empty,
                                    this.marqueeApi
                                        .getMarquees(request)
                                        .pipe(map((fixtures) => fixtures && this.marqueeResponseMapperService.mapFixtures(fixtures, tiles))),
                                ),
                            ),
                        ),
                        empty,
                    ),
                    iif(
                        () => params.sport === undefined && params.region === undefined && params.league === undefined,
                        empty,
                        from(
                            this.seoContentService.getForEventDetails({
                                filter: this.getDetailDataContext(this.router.params()),
                                sport: params.sport,
                                region: params.region,
                                league: this.competitionIdService.getCompetitionId(sport, params),
                                conference: params.conference,
                            }),
                        ),
                    ),
                    iif(
                        () => !!route.params.league && this.teamPagesConfig.isEnabled && !params.isVirtual,
                        this.teamPagesCoreService.getModelByCompetition(route, false, compoundId),
                        empty,
                    ),
                    iif(
                        () =>
                            includePostRequest &&
                            params.sport !== undefined &&
                            this.timeFilterConfig.isTimeFilterEnabled &&
                            this.calendar.getInterval(params.context) !== undefined,
                        this.calendarCacheService.getSportWithCalendar(params.sport!),
                        empty,
                    ),
                ]).pipe(
                    map(([fixtures = { fixtures: [], totalCount: 0 }, showcase, marqueeData, seoCompetitionTitle, teams, sportsCache]) => {
                        return {
                            ...fixtures,
                            params,
                            sport,
                            sorting,
                            tabs: this.getNavigation(sport, params, teams),
                            virtualCompetition: overview ? this.virtualCompetitionService.getCompetition(overview.sport, params) : undefined,
                            showcaseData: showcase && this.mapShowcaseFixtures(this.getShowcaseDataInfo(params), showcase.fixtures),
                            competitionTitle: seoCompetitionTitle?.h1Header,
                            marqueeData,
                            teampagesViewModel: teams,
                            calendarIntervals: this.showTimefilters(sportsCache) ? this.getLinks(params, sportsCache!) : undefined,
                            calendarPills: this.showTimefilters(sportsCache)
                                ? this.competitionTreeService.getCalendarPills(overview?.sport, params)
                                : undefined,
                            sportsOverview: overview?.sport,
                            calendarLiveCounts: sportsCache,
                        };
                    }),
                ),
            ),
        );
    }

    private mergeTargetToParams(params: CompetitionRoute, target: CompetitionRoute): CompetitionRoute {
        if (target.region) {
            params.region = target.region;
            params.regionName = target.regionName;
        }

        if (target.league) {
            params.league = target.league;
            params.isVirtual = target.isVirtual;
            params.leagueName = target.leagueName;
        }

        return params;
    }

    private getDetailDataContext({ context }: CompetitionRoute) {
        return keys(this.urlConfig.translations)
            .filter((prop) => this.urlConfig.translations[prop] === context)
            .pop();
    }

    private isWithoutFixtures(params: CompetitionRoute, sport?: CountItem) {
        return !sport || params.marketOffer === MarketOfferUrlParam.Standings;
    }

    private getShowcaseDataInfo(params: CompetitionRoute): LobbySpecificRequest {
        return {
            lobby: Lobby.Competition,
            sportId: params.sport,
            competitionId: params.league,
            regionId: params.region,
        };
    }

    private getShowcaseRequest(params: CompetitionRoute) {
        return this.showcaseRequestBuilderService.getRequestPayload(this.getShowcaseDataInfo(params));
    }

    getMarketGroupData(route: ActivatedRouteSnapshot): Observable<FixtureList> {
        const params = this.router.params();
        const empty = of(undefined);
        const sorting = this.getSorting(params);

        let marqueeRequest: IMarqueeRequestInfo;
        let realCompetitionId: number | undefined;
        const context = keys(this.urlConfig.translations)
            .filter((prop) => this.urlConfig.translations[prop] === this.router.params().context)
            .pop();

        const isWithoutFixtures = (sport?: CountItem) => !sport || params.marketOffer === MarketOfferUrlParam.Standings;

        // we have the cache implemented in the getData method so we do not care to check if load from cache or reload it
        return this.getData(route).pipe(
            map((overview) => ({
                sport: overview ? this.pruneHelperService.prune(overview.sport, params) : undefined,
                overview,
            })),
            tap(({ sport }) => {
                this.enhance(sport, params);
                realCompetitionId = this.competitionIdService.getCompetitionId(sport, params);
            }),
            switchMap(({ sport, overview }) =>
                combineLatest([
                    iif(() => isWithoutFixtures(sport), empty, this.service.getFixtureList(params, sorting)),
                    this.getMarqueeRequest(sport, params).pipe(
                        mergeMap((request) => {
                            marqueeRequest = request;

                            return iif(() => isEmpty(marqueeRequest.request), empty, this.marqueeApi.getMarquees(marqueeRequest.request));
                        }),
                    ),
                    iif(
                        () => params.sport === undefined && params.region === undefined && params.league === undefined,
                        empty,
                        from(
                            this.seoContentService.getForEventDetails({
                                filter: context,
                                sport: params.sport,
                                region: params.region,
                                league: realCompetitionId,
                            }),
                        ),
                    ),

                    iif(() => params.sport !== undefined, this.calendarCacheService.getSportWithCalendar(params.sport!), empty),
                ]).pipe(
                    map(([fixtures, marqueeInfo, seoCompetitionTitle]) => {
                        fixtures = fixtures || { fixtures: [], totalCount: 0 };

                        const marqueeData = marqueeInfo && this.marqueeResponseMapperService.mapFixtures(marqueeInfo, marqueeRequest.tiles);

                        return {
                            ...fixtures,
                            params,
                            sport,
                            sorting,
                            competitionTitle: seoCompetitionTitle?.h1Header,
                            marqueeData,
                            sportsOverview: overview?.sport,
                        };
                    }),
                ),
            ),
        );
    }

    private getCalendarIntervals(links: string[] | undefined): { [key: string]: IntervalFilter } {
        const intervals = this.calendar.getCalendarIntervals();

        keys(intervals).forEach((key) => {
            if (!links?.includes(intervals[key].name)) {
                delete intervals[key];
            }
        });

        return intervals;
    }

    private showTimefilters(sportsCache: CountItem | undefined): boolean {
        return !!sportsCache?.meta?.calendarIntervals?.length;
    }

    private getLinks(params: CompetitionRoute, sport: CountItem): IntervalLink[] {
        const time = now();
        const leaguePrefix = params.isVirtual ? VirtualCompetitionPrefix : undefined;
        const intervals = this.getCalendarIntervals(sport.meta.calendarIntervals);

        const links = keys(intervals)
            .map((key) =>
                this.calendar.getUrl(
                    key,
                    intervals[key]!,
                    sport,
                    params.context,
                    this.competitionTreeService.getUrlParameter(params.region, params.regionName),
                    this.competitionTreeService.getUrlParameter(params.league, params.leagueName, leaguePrefix),
                ),
            )
            .filter(isDefined);

        return orderBy(links, [(link) => (!link.to || link.to.getTime() < time ? time : link.to)], ['asc']);
    }

    getComponents(): MasterDetailComponent<CompetitionOverview | undefined>[] {
        return [
            {
                component: CompetitionNavigationComponent,
                map: (master?: CompetitionOverview) => ({
                    sport: master!.sport!,
                    tournaments: master!.tournaments!,
                    context: this.router.params(),
                }),
            },
        ];
    }

    getTarget(tree?: CompetitionOverview | undefined, route?: ActivatedRouteSnapshot): CompetitionRoute {
        const { context } = route?.params || this.router.params();

        if (!tree?.sport || tree.sport.children.length === 0) {
            return {};
        }

        const target: CompetitionRoute = {};
        const type = getItemTypeForSport(tree.sport.id);

        target.sport = tree.sport.id;
        target.sportName = tree.sport.name;
        target.context = context;

        const regionList = children(tree.sport, type);
        const region = this.getTopFallback(regionList);

        if (region && !this.calendar.isInterval(context)) {
            target.region = region.id;
            target.regionName = region.name;

            if (region.children.length === 1) {
                target.league = region.children[0].id;
                target.isVirtual = isVirtualCompetitionItem(region.children[0]);
                target.leagueName = region.children[0].name;
            }
        }

        return target;
    }

    protected override hasDependency(route: ActivatedRouteSnapshot, master: CompetitionOverview | undefined): boolean {
        const { subContext } = this.router.params();

        if (this.device.isRobot && subContext === this.urlConfig.translations.competitions) {
            return false;
        }

        return super.hasDependency(route, master);
    }

    private getMarqueeRequest(sport: CountItem | undefined, params: CompetitionRoute): Observable<IMarqueeRequestInfo> {
        const marqueeRequestParams = this.getMarqueeRequestParams(sport, params);

        return marqueeRequestParams
            ? this.marqueeRequestBuilderService.buildForCompetition(marqueeRequestParams.ids, marqueeRequestParams.sportId, marqueeRequestParams.type)
            : of(<IMarqueeRequestInfo>{
                  request: <MarqueeRequest>{
                      marqueeData: [],
                  },
                  tiles: [],
              });
    }

    private getMarqueeRequestParams(
        sport: CountItem | undefined,
        params: CompetitionRoute,
    ): { ids: number[]; type: CompetitionPageType; sportId: number } | undefined {
        if (!sport) {
            return;
        }

        if (params.league) {
            const leagues = children(sport, ItemType.Competition);
            const leagueIds = flatten(leagues.map((l) => l.siblings));

            return {
                ids: leagueIds,
                type: CompetitionPageType.Competition,
                sportId: params.sport!,
            };
        }

        return {
            ids: [params.region!],
            type: CompetitionPageType.Region,
            sportId: params.sport!,
        };
    }

    private enhance(sport: CountItem | undefined, params: CompetitionRoute, shouldRedirect = true): void {
        if (!sport) {
            return;
        }

        let extended = false;

        if (!params.marketCategory) {
            const id = this.gridHelper.getId(params);
            const state = this.gridStore.get(id);

            if (state) {
                const group = state.columns.find((current) => current.extended);

                if (group) {
                    extended = true;
                    params.marketCategory = toInteger(group.group);
                }
            }
        } else {
            extended = !params.marketOffer || params.marketOffer === MarketOfferUrlParam.Gridable;
        }

        const offerPriority = this.sportConfig.competitionOfferPriority;
        const offerOrder = offerPriority.overrides[sport.id] || offerPriority.default;
        const offerCount = {
            [MarketOfferUrlParam.Gridable]: sport.counts.gridable + sport.counts.nonGridable + sport.counts.other,
            [MarketOfferUrlParam.Outrights]: sport.counts.outrights,
            [MarketOfferUrlParam.Specials]: sport.counts.specials,
            [MarketOfferUrlParam.Standings]: this.hasLeagueStatistics(sport, params.virtualCompetitionGroup),
            [MarketOfferUrlParam.Teams]: this.teamPagesConfig.isEnabled,
        };

        if (params.marketOffer && !offerCount[params.marketOffer as keyof typeof offerCount] && shouldRedirect) {
            return this.redirectHelper.goToPage(this.router.path());
        }

        if (extended && offerCount.matches) {
            params.marketOffer = MarketOfferUrlParam.NonGridable;
        } else if (!params.marketOffer) {
            for (const offer of offerOrder) {
                if (offerCount[offer as keyof typeof offerCount]) {
                    params.marketOffer = offer;
                    break;
                }
            }
        }
    }

    private getNavigation(
        sport: CountItem | undefined,
        route: CompetitionRoute,
        teamPagesViewModel: TeamPagesViewModel | undefined,
    ): TabBarItem[] | undefined {
        if (!sport || !(route.context === this.urlConfig.translations.betting || route.context === this.urlConfig.translations.conferences)) {
            return;
        }

        const navigation: TabBarItem[] = [];

        const appendContext = { ...sport.counts, ...sport.meta };
        const append = (tab: FixtureTab, count: CountCallback) => {
            const value = count(appendContext);

            if (isNumber(value) && value > 0 && navigation) {
                //TODO any index issue, probably a defect, please investigate Teams vs Team key mismatch (remove cast to see the issue)
                //cast was needed due to Teams not matching the key Team
                const title = this.sitecore.event[ReverseFixtureTab[tab] as keyof typeof this.sitecore.event];
                navigation.push({ id: tab, title, count: value });
            }
        };

        append(FixtureTab.Matches, (x) => x.preMatch + x.live - x.outrights);
        append(FixtureTab.Outrights, (x) => x.outrights);
        append(FixtureTab.Specials, (x) => x.specials);
        if (
            this.teamPagesCoreService.isTeamPagesEnabledForSport(sport.id) &&
            route.league &&
            teamPagesViewModel?.teamPagesNavigationViewModel.teams?.length
        ) {
            navigation.push({ id: FixtureTab.Teams, title: this.sitecore.globalMessages.Teams });
        }

        if (this.hasLeagueStatistics(sport, route.virtualCompetitionGroup)) {
            navigation.push({ id: FixtureTab.Standings, title: this.sitecore.event.Standings });
        }

        if (route.marketOffer) {
            const matchingOffer = new Map<string, string>([[MarketOfferUrlParam.NonGridable, MarketOfferUrlParam.Gridable]]);
            const marketOffer = matchingOffer.get(route.marketOffer) || route.marketOffer;

            navigation.forEach((item) => (item.active = lowerCase(FixtureTab[item.id]) === marketOffer));
        } else {
            navigation.forEach((item, index) => (item.active = index === 0));
        }

        return navigation;
    }

    // Checking if any leafs have standings
    private hasLeagueStatistics(sport: CountItem, group?: number): boolean {
        if (sport.id !== SportConstant.Soccer) {
            return false;
        }

        const leagues = children(sport, ItemType.Competition);

        if (!group && leagues.some((league) => league.standings && (!league.isVirtual || league.children.length > 0))) {
            return true;
        }

        const groups = children(leagues, ItemType.VirtualCompetitionGroup);

        if (groups.some((league) => league.standings)) {
            return true;
        }

        return false;
    }

    private getTop(source: CountItem[]): CountItem | undefined {
        return source
            .filter((x) => x.order !== undefined)
            .sort((first, second) => first.order! - second.order!)
            .shift();
    }

    private getTopFallback(source: CountItem[]): CountItem {
        const top = this.getTop(source);

        if (top) {
            return top;
        }

        return source[0];
    }

    private getSorting(params: CompetitionRoute): GridSorting {
        if (params.marketOffer === MarketOfferUrlParam.Outrights || params.marketOffer === MarketOfferUrlParam.Specials) {
            return GridSorting.Competition;
        }

        if (params.isVirtual) {
            return this.sortingStateService.getVirtualCompetition();
        }

        const competitionSorting = this.sortingStateService.get('competitions', params.sport);

        if (competitionSorting === undefined) {
            return GridSorting.Competition;
        }

        return competitionSorting;
    }

    private mapShowcaseFixtures(showcaseDataInfo: LobbySpecificRequest, fixtures?: ShowcaseFixtureData[]): ShowcaseModuleData | undefined {
        if (isEmpty(fixtures)) {
            return;
        }
        const mappedFixtures = this.showcaseService.getLobbySpecificMappedData(showcaseDataInfo, fixtures!);
        const competitionId = this.getCompetitionId(showcaseDataInfo);

        return {
            ...mappedFixtures,
            events: mappedFixtures.events.filter((f) => competitionId === f.event.league.id || competitionId === f.event.league.parentLeagueId),
        };
    }

    private getCompetitionId(data: LobbySpecificRequest): number | undefined {
        if (isNumber(data.competitionId) && data.competitionId > 0) {
            return data.competitionId;
        } else if (Array.isArray(data.competitionId) && data.competitionId.length) {
            return data.competitionId.find((id) => id > 0);
        }

        return;
    }
}
